import { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import PropTypes from "prop-types";
import Joyride, { ACTIONS, EVENTS, STATUS } from "react-joyride";
import steps from "./guide-steps";
import Button from "../button/button.index";
import { ButtonType } from "../button/button.interface";
import PATH from "../../../urls/path";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { isTouchScreen } from "../../../scripts/helpers";
import { setNewLoginStatus } from "../../../redux/slices/newLoginSlice";
import { setMobileSidebarStatus } from "../../../redux/slices/layoutSlice";
import store from "../../../redux/store";
import { shallowEqual } from "react-redux";

const Guide = forwardRef(({ isLocalStorage }: any, ref) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { inputUrl } = useAppSelector(({ newLogin: { inputUrl } }) => ({ inputUrl }), shallowEqual);
    const [runGuide, setRunGuide] = useState(!isLocalStorage ? true : store.getState().newLogin.newLogin);
    const [stepIndex, setStepIndex] = useState(0);
    useImperativeHandle(ref, () => ({
        startGuideFunc() {
            if (!runGuide) {
                setRunGuide(true);
            }
        },
        isGuideRunning() {
            return runGuide;
        }
    }));

    const onClick = useCallback((evt: any) => {}, []);

    const endGuide = (index: number) => {
        setRunGuide(false);
        setStepIndex(0);
        dispatch(setNewLoginStatus(false));
        if (isTouchScreen()) {
            dispatch(setMobileSidebarStatus(false));
        }
        navigate(inputUrl);
        document.removeEventListener("mouseup", onClick);
    };

    const readyToProceed = (newStepIndex: any) => {
        if (newStepIndex < (steps as any).length) {
            const stepName = (steps as any)[newStepIndex].step;
            if (stepName === "new-upload" || stepName === "new-upload-link") {
                if (!isTouchScreen()) {
                    const uploadMenu = document.getElementById("upload-menu-button");
                    if (uploadMenu) uploadMenu.click();
                }
                navigate(PATH.files);
            } else if (stepName === "new-upload-mobile" || stepName === "new-upload-link-mobile") {
                navigate(PATH.files);
                if (isTouchScreen() && stepName === "new-upload-link-mobile") {
                    const uploadMenu = document.getElementById("upload-link-url-mobile");
                    if (uploadMenu) uploadMenu.click();
                }
            } else if (stepName === "contact-sidebar") {
                if (isTouchScreen()) {
                    dispatch(setMobileSidebarStatus(true));
                    const uploadMenu = document.getElementById("upload-link-url-backdrop-mobile");
                    if (uploadMenu) uploadMenu.click();
                } else navigate(PATH.contact);
            } else if (stepName === "report") {
                if (isTouchScreen()) dispatch(setMobileSidebarStatus(true));
                if (!isTouchScreen()) navigate(PATH.report);
            }
        }
        return true;
    };

    const handleJoyrideCallback = (data: any) => {
        const { action, index, type, status } = data;
        if ([EVENTS.TOUR_START].includes(type)) {
            document.addEventListener("mouseup", onClick);
        } else if ([ACTIONS.START].includes(action) && index === 0) {
            document.addEventListener("mouseup", onClick);
        } else if ([ACTIONS.CLOSE].includes(action) || [STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
            if (runGuide) {
                endGuide(index);
            }
        } else if ([EVENTS.STEP_AFTER].includes(type)) {
            const newStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);
            if (action !== ACTIONS.PREV && !readyToProceed(newStepIndex)) {
                setRunGuide(false);
                setStepIndex(index);
                setTimeout(() => setRunGuide(true), 400);
            } else {
                dispatch(setNewLoginStatus(false));
                setRunGuide(false);
                setStepIndex(newStepIndex);
                setTimeout(() => setRunGuide(true), 400);
            }
        } else if ([EVENTS.TARGET_NOT_FOUND].includes(type)) {
            // The target could not be found. Go to next step.
            const newStepIndex = index + 1;
            setStepIndex(newStepIndex);
        }
    };

    const Tooltip = ({ continuous, index, step, backProps, closeProps, primaryProps, tooltipProps }: any) => (
        <div className="flex flex-col items-center w-[300px] gap-[28px] bg-white rounded-[12px]" {...tooltipProps}>
            <div className="pt-[23px] px-[27px]">{step.content}</div>
            <div className="flex items-center justify-between w-full px-[27px] pb-[23px]">
                <Button
                    textClassName={"tour"}
                    className={`tour ${index >= 4 && `w-full`}`}
                    type={index < 4 ? ButtonType.TERTIARY : ButtonType.PRIMARY}
                    text={index < 4 ? "بی‌خیال" : "متوجه شدم"}
                    {...closeProps}
                />
                {continuous && index < 4 && (
                    <Button textClassName={"tour"} text="بعدی" className={`w-[157px] tour step=${String(index)}`} {...primaryProps} />
                )}
            </div>
        </div>
    );

    return (
        <Joyride
            spotlightClicks={false}
            continuous
            stepIndex={stepIndex}
            showProgress
            showSkipButton
            disableOverlayClose
            tooltipComponent={Tooltip}
            run={runGuide}
            callback={handleJoyrideCallback}
            styles={{
                buttonBack: {
                    display: "block",
                    color: "#4E4B66"
                },
                options: {
                    primaryColor: "#398ab5"
                },
                tooltip: {
                    width: "300px"
                },
                tooltipContainer: {
                    textAlign: "start"
                },
                buttonClose: {
                    display: "none"
                },
                buttonNext: {
                    backgroundColor: "#2991FF",
                    width: "157px",
                    height: "40px",
                    borderRadius: "8px",
                    display: "none"
                },
                buttonSkip: {
                    color: "#398ab5"
                }
            }}
            steps={steps as any}
        />
    );
});

Guide.propTypes = {
    isLocalStorage: PropTypes.bool
};

Guide.defaultProps = {
    isLocalStorage: true
};

export default Guide;
